import React, { useEffect, useReducer, useState } from 'react'
import isFunction from 'lodash/isFunction'
import { PERMISSIONS } from 'src/config/roleConfig'
import { useHasRequiredPermissions } from 'src/app/AppAuth/AppAuth.hooks'
import {
  StandardButton,
  StandardButtonVariant,
  StandardButtonSize,
} from '../Buttons'
import { ButtonGroup, EditableOnClickContainer } from './Inputs.styles'
import { EditOnClickProps } from './inputs.constants'

export const EditOnClick = ({
  isInline,
  editMode,
  onSave,
  onSaveControlClose,
  onCancel,
  isEmpty,
  children,
  requiredPermissions = [PERMISSIONS.READ, PERMISSIONS.EDIT],
  lastSaveDate,
  onClickConfirmationModal,
}: EditOnClickProps): JSX.Element => {
  const [isEditing, toggleEditMode] = useReducer((prev) => !prev, false)
  const [isSaving, setIsSaving] = useState(false)
  const hasPermissions = useHasRequiredPermissions(requiredPermissions)

  const onSaveFunc: React.MouseEventHandler = async (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault()
    e.stopPropagation()
    setIsSaving(true)
    if (isFunction(onSave)) {
      onSave()
      toggleEditMode()
      setIsSaving(false)
    } else if (isFunction(onSaveControlClose)) {
      const close = await onSaveControlClose()
      close && toggleEditMode()
      setIsSaving(false)
    }
  }

  useEffect(() => {
    if (lastSaveDate) {
      toggleEditMode()
    }
  }, [lastSaveDate, toggleEditMode])

  const onCancelFunc = () => {
    isFunction(onCancel) && onCancel()
    toggleEditMode()
  }

  const onClick = () => {
    if (isEditing) {
      return
    }
    if (isFunction(onClickConfirmationModal)) {
      onClickConfirmationModal(() => toggleEditMode())
    } else {
      toggleEditMode()
    }
  }

  return (
    <EditableOnClickContainer
      inline={isInline}
      isEditing={isEditing}
      isEmpty={isEmpty}
      onClick={onClick}
    >
      {hasPermissions && isEditing ? (
        <>
          {editMode}
          <ButtonGroup inline={isInline}>
            <StandardButton
              variant={StandardButtonVariant.SECONDARY}
              buttonSize={StandardButtonSize.SMALL}
              onClick={onCancelFunc}
            >
              Cancel
            </StandardButton>
            <StandardButton
              variant={StandardButtonVariant.PRIMARY}
              buttonSize={StandardButtonSize.SMALL}
              onClick={onSaveFunc}
              disabled={isSaving}
            >
              Save
            </StandardButton>
          </ButtonGroup>
        </>
      ) : (
        <>{children}</>
      )}
    </EditableOnClickContainer>
  )
}
